import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiService } from 'app/shared/apis/api.service';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export interface UserDocumentSignature {
    user_document_signature_id: number;
    file_module_id: number | null;
    user_id: number;
    token: string;
    signed_pdf_path: string | null;
    signer_name: string | null;
    signer_document: string | null;
    signed_at: Date | null;
    ip_address: string | null;
    status: string;
    expires_at: Date | null;
    createdAt: Date;
}

export interface UserDocumentSignatureListItem {
    file_module: {
        file_module_id: number;
        path: string;
        name: string;
        type_file: string;
        module: string;
        entity_id: number;
        createdAt: Date;
    };
    signature: UserDocumentSignature | null;
}

export interface UserDocumentSignatureFileInfo {
    user_document_signature_file_id?: number | null;
    file_module_id: number;
    file_name: string | null;
    file_path: string | null;
    file_type: 'pdf' | 'image' | 'other';
    signed_pdf_path?: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class UserDocumentSignatureService {
    constructor(
        private api: ApiService,
        private http: HttpClient,
    ) {}

    createLink(file_module_id: number): Observable<{
        user_document_signature: UserDocumentSignature;
        signature_url: string;
    }> {
        return this.api.postSub(`userDocumentSignature/create-link/${file_module_id}`, {});
    }

    createLinkBatch(file_module_ids: number[]): Observable<{
        user_document_signature: UserDocumentSignature;
        signature_url: string;
    }> {
        return this.api.postSub(`userDocumentSignature/create-link`, { file_module_ids });
    }

    sendEmail(file_module_id: number): Observable<{
        success: boolean;
        message: string;
        email_sent: string;
        signature_url: string;
    }> {
        return this.api.postSub(`userDocumentSignature/send-email/${file_module_id}`, {});
    }

    sendEmailBatch(file_module_ids: number[]): Observable<{
        success: boolean;
        message: string;
        email_sent: string;
        signature_url: string;
    }> {
        return this.api.postSub(`userDocumentSignature/send-email`, { file_module_ids });
    }

    listByUser(user_id: number): Observable<{ items: UserDocumentSignatureListItem[] }> {
        return this.api.getSub(`userDocumentSignature/by-user/${user_id}`);
    }

    getByFile(file_module_id: number): Observable<{
        has_signature: boolean;
        user_document_signature?: UserDocumentSignature;
    }> {
        return this.api.getSub(`userDocumentSignature/by-file/${file_module_id}`);
    }

    getInfo(token: string): Observable<{
        valid: boolean;
        message?: string;
        already_signed?: boolean;
        user_document_signature?: {
            token: string;
            user_id: number;
            expires_at: Date;
            status: string;
            files: UserDocumentSignatureFileInfo[];
            file_module_id?: number;
            file_name?: string;
            file_path?: string;
            file_type?: 'pdf' | 'image' | 'other';
        };
        signer_defaults?: { signer_name: string; signer_document: string };
    }> {
        return this.api.getSub(`public/document-signature/info/${token}`);
    }

    getFileBlob(token: string, file_module_id?: number): Observable<Blob> {
        const path =
            file_module_id != null
                ? `public/document-signature/file/${token}/${file_module_id}`
                : `public/document-signature/file/${token}`;
        return this.http.get(`${environment.apiUrl}/${path}`, { responseType: 'blob' });
    }

    processSignature(
        token: string,
        data: {
            signer_name: string;
            signer_document: string;
            signature_image: string;
            signature_position_x?: number;
            signature_position_y?: number;
            signature_page?: number;
        },
    ): Observable<{ success: boolean; message: string; user_document_signature: UserDocumentSignature }> {
        return this.api.postSub(`public/document-signature/sign/${token}`, data);
    }
}
